/**
 * Web Push (PWA) — Settings UI, subscribe/unsubscribe, deep-link navigation.
 * Opt-in only; never enables automatically.
 *
 * One browser PushSubscription endpoint must belong to at most one account.
 * On logout / Switch Account we detach this device from the previous user, then
 * re-bind only if the new account has push enabled.
 */
import { syncToggleSwitch } from "./toggle-switch";
import { isPwaStandalone } from "../pwa-detect";
import {
  deletePushSubscription,
  fetchPushPrefs,
  fetchPushVapid,
  patchPushPrefs,
  registerPushSubscription,
  type PushPrefsDto,
} from "../lib/cloud";

export type PushNavScreen =
  | "home"
  | "shop"
  | "friends"
  | "messages"
  | "notifications"
  | "settings";

export type PushNotificationsMount = {
  isSignedIn: () => boolean;
  getUserId?: () => string | null;
  openShop: () => void;
  openFriends: () => void;
  openMessages: () => void;
  openNotifications: () => void;
  openSettingsNotifications: () => void;
  goHome: () => void;
};

const DEFAULT_PREFS: PushPrefsDto = {
  enabled: false,
  friends: true,
  messages: true,
  moderation: true,
  shop: true,
  events: true,
  crates: true,
  playStreak: true,
  dailyReward: true,
  announcements: true,
};

let mount: PushNotificationsMount | null = null;
let prefs: PushPrefsDto = { ...DEFAULT_PREFS };
let vapidConfigured = false;
let busy = false;
/** Last account this device's endpoint was synced for (client-side). */
let boundUserId: string | null = null;

const CATEGORY_KEYS = [
  "friends",
  "messages",
  "moderation",
  "shop",
  "events",
  "crates",
  "playStreak",
  "dailyReward",
  "announcements",
] as const;

type CategoryKey = (typeof CATEGORY_KEYS)[number];

function isIosLike(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (/iPad|iPhone|iPod/i.test(ua)) return true;
  return navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1;
}

function pushSupported(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

function el<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

function setStatus(msg: string, isError = false): void {
  const status = el<HTMLParagraphElement>("settingsPushStatus");
  if (!status) return;
  status.textContent = msg;
  status.classList.toggle("formError", isError);
  status.classList.toggle("accessHint", !isError);
  status.classList.toggle("hidden", !msg);
}

function setIosHintVisible(show: boolean): void {
  const hint = el("settingsPushIosHint");
  if (hint) hint.classList.toggle("hidden", !show);
}

function syncCategoryDisabled(): void {
  const masterOn = prefs.enabled;
  for (const key of CATEGORY_KEYS) {
    const btn = el(`togglePush${capitalize(key)}Btn`);
    if (!btn) continue;
    btn.toggleAttribute("disabled", !masterOn || busy);
    btn.style.opacity = masterOn ? "1" : "0.45";
  }
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function syncAllToggles(): void {
  syncToggleSwitch(el("togglePushEnabledBtn"), prefs.enabled);
  for (const key of CATEGORY_KEYS) {
    const btnId = `togglePush${capitalize(key)}Btn`;
    syncToggleSwitch(el(btnId), prefs[key] !== false);
  }
  syncCategoryDisabled();
}

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function getRegistration(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
}

async function currentSubscription(): Promise<PushSubscription | null> {
  const reg = await getRegistration();
  if (!reg?.pushManager) return null;
  try {
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/** Tell the SW which account is active so it can drop mismatched pushes. */
export function setPushServiceWorkerUser(userId: string | null): void {
  if (!("serviceWorker" in navigator)) return;
  const id = userId ? String(userId) : null;
  const post = (sw: ServiceWorker | null | undefined) => {
    try {
      sw?.postMessage({ type: "PUSH_SET_USER", userId: id });
    } catch {
      /* ignore */
    }
  };
  post(navigator.serviceWorker.controller);
  void navigator.serviceWorker.ready
    .then((reg) => {
      post(reg.active);
      post(reg.waiting);
      post(reg.installing);
    })
    .catch(() => {});
}

/**
 * Detach this browser endpoint from the currently signed-in user (server row only).
 * Keeps the PushManager subscription so the next account can re-bind quickly.
 * Call while the previous account session cookie is still valid (before logout/switch).
 */
export async function releasePushDeviceForSession(): Promise<void> {
  setPushServiceWorkerUser(null);
  boundUserId = null;
  try {
    const sub = await currentSubscription();
    if (sub?.endpoint) {
      await deletePushSubscription(sub.endpoint);
    }
  } catch {
    /* ignore — best-effort isolation */
  }
}

async function ensureBrowserSubscription(): Promise<PushSubscription | null> {
  const vapid = await fetchPushVapid();
  vapidConfigured = vapid.configured;
  if (!vapid.configured || !vapid.publicKey) return null;
  if (typeof Notification !== "undefined" && Notification.permission !== "granted") {
    return null;
  }
  const reg = await getRegistration();
  if (!reg?.pushManager) return null;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid.publicKey) as BufferSource,
    });
  }
  return sub;
}

async function subscribeDevice(): Promise<boolean> {
  const vapid = await fetchPushVapid();
  if (!vapid.configured || !vapid.publicKey) {
    setStatus("Push is not configured on the server yet.", true);
    return false;
  }
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    setStatus("Notification permission was denied.", true);
    return false;
  }
  const reg = await getRegistration();
  if (!reg?.pushManager) {
    setStatus("Service worker is not ready.", true);
    return false;
  }
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapid.publicKey) as BufferSource,
    });
  }
  await registerPushSubscription(sub.toJSON());
  return true;
}

async function unsubscribeDeviceFully(): Promise<void> {
  const sub = await currentSubscription();
  const endpoint = sub?.endpoint;
  if (sub) {
    try {
      await sub.unsubscribe();
    } catch {
      /* ignore */
    }
  }
  try {
    await deletePushSubscription(endpoint || undefined);
  } catch {
    /* ignore */
  }
  boundUserId = null;
  setPushServiceWorkerUser(null);
}

/**
 * After login / Switch Account / session refresh: bind this device to the
 * current account only when that account has push enabled; otherwise stay detached.
 */
export async function syncPushSubscriptionForSession(userId: string | null): Promise<void> {
  const id = userId ? String(userId) : null;
  setPushServiceWorkerUser(id);

  if (!id || !mount?.isSignedIn()) {
    prefs = { ...DEFAULT_PREFS };
    boundUserId = null;
    syncAllToggles();
    return;
  }

  if (!pushSupported()) return;

  try {
    const vapid = await fetchPushVapid();
    vapidConfigured = vapid.configured;
    const data = await fetchPushPrefs();
    prefs = data.prefs;

    if (prefs.enabled && vapidConfigured && Notification.permission === "granted") {
      const sub = await ensureBrowserSubscription();
      if (sub) {
        await registerPushSubscription(sub.toJSON());
        boundUserId = id;
      }
    } else if (boundUserId === id || boundUserId) {
      // Account has push off: ensure this endpoint is not left registered to them.
      const sub = await currentSubscription();
      if (sub?.endpoint) {
        try {
          await deletePushSubscription(sub.endpoint);
        } catch {
          /* ignore */
        }
      }
      boundUserId = null;
    }
    syncAllToggles();
  } catch {
    syncAllToggles();
  }
}

async function enableMaster(): Promise<void> {
  if (!mount?.isSignedIn()) {
    setStatus("Sign in to enable push notifications.", true);
    syncToggleSwitch(el("togglePushEnabledBtn"), false);
    prefs.enabled = false;
    return;
  }
  if (!pushSupported()) {
    setStatus("This browser does not support Web Push.", true);
    syncToggleSwitch(el("togglePushEnabledBtn"), false);
    prefs.enabled = false;
    return;
  }
  if (isIosLike() && !isPwaStandalone()) {
    setIosHintVisible(true);
    setStatus("On iPhone/iPad, add CursorGame to your Home Screen first.", true);
    syncToggleSwitch(el("togglePushEnabledBtn"), false);
    prefs.enabled = false;
    return;
  }
  setIosHintVisible(false);
  if (!vapidConfigured) {
    const vapid = await fetchPushVapid();
    vapidConfigured = vapid.configured;
    if (!vapidConfigured) {
      setStatus("Push is not configured on the server yet.", true);
      syncToggleSwitch(el("togglePushEnabledBtn"), false);
      prefs.enabled = false;
      return;
    }
  }

  busy = true;
  syncCategoryDisabled();
  try {
    const ok = await subscribeDevice();
    if (!ok) {
      prefs.enabled = false;
      syncAllToggles();
      return;
    }
    const res = await patchPushPrefs({ enabled: true });
    prefs = res.prefs;
    const uid = mount?.getUserId?.() || null;
    boundUserId = uid;
    setPushServiceWorkerUser(uid);
    setStatus(
      "Push notifications enabled on this device. Tip: minimize or hide the tab — some browsers suppress alerts while CursorGame is focused.",
    );
    syncAllToggles();
  } catch (e) {
    prefs.enabled = false;
    setStatus(e instanceof Error ? e.message : "Could not enable push.", true);
    syncAllToggles();
  } finally {
    busy = false;
    syncCategoryDisabled();
  }
}

async function disableMaster(): Promise<void> {
  busy = true;
  syncCategoryDisabled();
  try {
    await unsubscribeDeviceFully();
    const res = await patchPushPrefs({ enabled: false });
    prefs = res.prefs;
    setStatus("Push notifications disabled.");
    syncAllToggles();
  } catch (e) {
    setStatus(e instanceof Error ? e.message : "Could not disable push.", true);
  } finally {
    busy = false;
    syncCategoryDisabled();
  }
}

async function toggleCategory(key: CategoryKey): Promise<void> {
  if (!prefs.enabled || busy) return;
  if (!mount?.isSignedIn()) {
    setStatus("Sign in to change notification categories.", true);
    return;
  }
  const next = !prefs[key];
  prefs = { ...prefs, [key]: next };
  syncAllToggles();
  busy = true;
  try {
    const res = await patchPushPrefs({ [key]: next });
    prefs = res.prefs;
    setStatus("");
  } catch (e) {
    prefs = { ...prefs, [key]: !next };
    syncAllToggles();
    setStatus(e instanceof Error ? e.message : "Could not save preference.", true);
  } finally {
    busy = false;
    syncCategoryDisabled();
  }
}

export async function refreshPushSettingsUi(): Promise<void> {
  setIosHintVisible(isIosLike() && !isPwaStandalone());
  if (!mount?.isSignedIn()) {
    prefs = { ...DEFAULT_PREFS };
    syncAllToggles();
    setStatus("Sign in to manage push notifications.");
    return;
  }
  try {
    const vapid = await fetchPushVapid();
    vapidConfigured = vapid.configured;
    if (!vapidConfigured) {
      setStatus("Push is not configured on the server (missing VAPID keys).", true);
    } else {
      setStatus("");
    }
    const data = await fetchPushPrefs();
    prefs = data.prefs;
    syncAllToggles();
  } catch {
    syncAllToggles();
  }
}

export function navigateFromPushScreen(screen: string): void {
  if (!mount) return;
  const s = String(screen || "home").toLowerCase() as PushNavScreen;
  switch (s) {
    case "shop":
      mount.openShop();
      break;
    case "friends":
      mount.openFriends();
      break;
    case "messages":
      mount.openMessages();
      break;
    case "notifications":
      mount.openNotifications();
      break;
    case "settings":
      mount.openSettingsNotifications();
      break;
    case "home":
    default:
      mount.goHome();
      break;
  }
}

function consumePushQuery(): void {
  try {
    const url = new URL(window.location.href);
    const push = url.searchParams.get("push");
    if (!push) return;
    url.searchParams.delete("push");
    window.history.replaceState({}, "", url.pathname + url.search + url.hash);
    window.setTimeout(() => navigateFromPushScreen(push), 400);
  } catch {
    /* ignore */
  }
}

function bindToggles(): void {
  const master = el("togglePushEnabledBtn");
  if (master) {
    master.onclick = () => {
      if (busy) return;
      if (prefs.enabled) void disableMaster();
      else void enableMaster();
    };
  }
  for (const key of CATEGORY_KEYS) {
    const btn = el(`togglePush${capitalize(key)}Btn`);
    if (!btn) continue;
    btn.onclick = () => void toggleCategory(key);
  }
}

export function mountPushNotificationSettings(opts: PushNotificationsMount): void {
  mount = opts;
  bindToggles();
  syncAllToggles();
  void refreshPushSettingsUi();
  consumePushQuery();

  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", (event) => {
      const data = event.data;
      if (!data || data.type !== "PUSH_NAV") return;
      navigateFromPushScreen(String(data.screen || "home"));
    });
  }
}
